import {
  ArrowBack,
  ButtonsContainer,
  FieldContainer,
  FormContainer,
  FormWrapper,
  HeaderButtonsContainer,
  SectionContainer,
} from "./MemberForm.styled";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  HomeOutlined,
  InfoOutlined,
  ContactsOutlined,
  ReceiptLong,
  Paid,
  WorkspacePremiumOutlined,
  History,
  GroupsOutlined
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  FormLabel,
  Snackbar,
  Typography,
} from "@mui/material";
import InputField from "../../components/formFields/InputField";
import {
  useCreateMemberMutation,
  useDeleteMemberMutation,
  useGetAffiliatesQuery,
  useGetLicenseTypesQuery,
  useGetMemberByIdQuery,
  useGetNaicMemberQuery,
  useGetStatesQuery,
  useUpdateMemberMutation,
} from "../../services/InsuranceService";
import { useLocation, useNavigate } from "react-router-dom";

import DatePickerField from "../../shared/components/DatePickerField";
import CheckboxField from "../../components/formFields/CheckboxField";
import { InfoPopup } from "../../shared/components/InfoPopup";
import { LoadingPanel } from "../../shared/components/LoadingPanel";
import SelectField from "../../shared/components/SelectField";
import { useEffect, useState } from "react";
import StyledGrid from "../../shared/components/StyledGrid";
import { affiliatesColumns } from "../../constants/TablesColumns";
import { naicMemberRoleCheck, getEditUsersPermission } from "../../shared/utils/permissions";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import useFetchWithMsal from "../../utils/useFetchWithMsal";
import { InvoicesTab } from "../tabs/invoicesTab/InvoicesTab";
import { MemberHistoryTab } from "../tabs/memberHistoryTab/MemberHistoryTab";
import { LicensesTab } from "../tabs/licensesTab/LicensesTab";
import SearchableSelect from "../../components/formFields/SearchableSelect";
import { PaymentsTab } from "../tabs/paymentsTab/PaymentsTab";
import { setActiveTab, setInvoiceNumber } from "../../store/slices/FilterSlice";
import { ChangeGroupMemberPopup } from "./changeGroupMemberPopup/ChangeGroupMemberPopup";

const MemberForm = ({ type }) => {
  const location = useLocation();

  const roleId = useSelector((state) => state?.user?.roleId);
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const { skip } = useFetchWithMsal();
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const { data } = useGetMemberByIdQuery(id, {
    skip: type === "create" || skip,
    refetchOnMountOrArgChange: true,
  });
  // Lazy load affiliates only when the Affiliates accordion (panel 9) is expanded
  const [expanded, setExpanded] = useState([1]);
  const affiliatesExpanded = expanded.includes(9);
  const { data: affiliatesData } = useGetAffiliatesQuery(id, {
    skip: type === "create" || skip || !affiliatesExpanded,
  });
  const { data: states } = useGetStatesQuery("");
  const { data: licensesTypes } = useGetLicenseTypesQuery("", {});

  const { data: naicMembers } = useGetNaicMemberQuery("", {});

  const naicMembersWithoutAllValue = () => naicMembers?.slice(1);

  const [updateMember, { isLoading }] = useUpdateMemberMutation("");
  const [deleteMember, { isLoading: isLoadingDeleteMember, isSuccess }] =
    useDeleteMemberMutation("");

  const [createMember, { isLoading: isLoadingCreateMember }] =
    useCreateMemberMutation("");
  const navigate = useNavigate();

  const [alertMessage, setAlertMessage] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [initialValues, setInitialValues] = useState({
    naicCode: "",
    name: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
    commenceDate: null,
    businessType: "",
    federalId: "",
    domicileState: "",
    phone: "",
    attention: "",
    assessmentEmail: "",
    proxyEmail: "",
    currentAccountInfo: 0,
    comments: "",
    mergedFlag: false,
    insolventFlag: false,
    suspendedFlag: false,
    activeFlag: false,
    abatementFlag: false,
    deferredFlag: false,
    mergedMemberValue: 1,
  });
  // moved expanded initialization above for lazy loading logic

  const [disableFilterReset, setDisableFilterReset] = useState(false);

  const mergedMemberValue = naicMembersWithoutAllValue()?.find(
    (item) => item.id === initialValues?.mergedMember
  );
  useEffect(() => {
    if (data) {
      setInitialValues(prev => ({
        ...prev,
        ...data,
        mergedMemberValue: mergedMemberValue,
      }));
    }
  }, [data, mergedMemberValue]);

  useEffect(() => {
    if (isSuccess) {
      navigate("/");
    }
  }, [isSuccess]);
  const phoneRegEx =
    /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;
  const validationSchema = Yup.object().shape({
    phone: Yup.string().nullable().matches(phoneRegEx, "Phone is not valid"),

    assessmentEmail: Yup.string()
      .nullable()
      .test("is-valid-emails", "Email is not valid", (value) => {
        if (!value) return true;
        const emails = value
          .trim()
          .split(/\s+/)
          .map((email) => email.trim());
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails.every((email) => emailRegex.test(email));
      }),
    proxyEmail: Yup.string()
      .nullable()
      .test("is-valid-emails", "Email is not valid", (value) => {
        if (!value) return true;
        const emails = value.split(" ").map((email) => email.trim());
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails.every((email) => emailRegex.test(email));
      }),
  });
  const handleSubmit = async (values) => {
    try {
      let response;
      if (type === "edit") {
        response = await updateMember({
          ...values,
          mergedMember: values?.mergedMemberValue?.id,
        });
      } else {
        response = await createMember({
          ...values,
          mergedMember: values?.mergedMemberValue?.id,
        });
      }
      if (response?.error) {
        throw new Error(
          response.error.data?.Message || "Unknown error occurred"
        );
      }
      setAlertMessage(
        type === "edit" ? (
          <>
            {`Member ${values?.naicCode} changes saved successfully.`} <br />
            {response?.data?.message}
          </>
        ) : (
          "Member created successfully."
        )
      );
      setSnackbarSeverity("success");
    } catch (error) {
      setAlertMessage(error.message);
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const onDeleteMemberClick = () => {
    setIsOpenPopup(true);
  };
  const handleDeleteMember = () => {
    deleteMember(id);
    setIsOpenPopup(false);
  };
  const onAccordionClick = (panel) => () => {
    if (expanded.includes(panel)) {
      setExpanded(expanded.filter((item) => item !== panel));
    } else {
      setExpanded([panel]);
    }
  };
  const onRedirect = (type, value) => {
    switch (type) {
      case "invoices":
        dispatch(setActiveTab(3));
        break;
      default:
        dispatch(setActiveTab(5));
        break;
    }
    setDisableFilterReset(true);
    dispatch(setInvoiceNumber(value));
    navigate("/");
  };
  const getInputComponent = (item) => {
    switch (item?.type) {
      case "date":
        return (
          <Field name={item.name}>
            {({ field, form }) => (
              <FieldContainer>
                <DatePickerField
                  name={item.name}
                  label={item.label}
                  value={field.value}
                  setValue={form.setFieldValue}
                  error={form.errors[item.name]}
                  maxDate={new Date()}
                  width={"100%"}
                  disabled={item.isDisabled}
                />
              </FieldContainer>
            )}
          </Field>
        );
      case "select":
        return (
          <Field name={item.name}>
            {({ field }) => (
              <FieldContainer>
                <SelectField
                  name={field.name}
                  label={item.label}
                  data={states}
                  fullWidth
                  isDisabled={item.isDisabled}
                />
              </FieldContainer>
            )}
          </Field>
        );
      case "licenseType":
        return (
          <Field name={item.name}>
            {({ field }) => (
              <FieldContainer>
                <SelectField
                  name={field.name}
                  label={item.label}
                  data={licensesTypes}
                  fullWidth
                  isDisabled={item.isDisabled}
                />
              </FieldContainer>
            )}
          </Field>
        );
      case "number":
        return (
          <Field name={item.name}>
            {({ field }) => (
              <FieldContainer>
                <InputField
                  name={item.name}
                  label={item.label}
                  mode="numeric"
                  value={field.value || ""}
                  maxLength={item.maxLength}
                  isDisabled={item.isDisabled}
                />
              </FieldContainer>
            )}
          </Field>
        );
      case "amount":
        return (
          <Field name={item.name}>
            {({ field }) => (
              <FieldContainer>
                <InputField
                  name={item.name}
                  label={item.label}
                  startAdornment={"$"}
                  mode="numeric"
                  value={field.value || ""}
                  maxLength={item.maxLength}
                  isDisabled={item.isDisabled}
                />
              </FieldContainer>
            )}
          </Field>
        );
      case "textarea":
        return (
          <Field name={item.name}>
            {({ field }) => (
              <FieldContainer>
                <InputField
                  name={item.name}
                  label={item.label}
                  type="textarea"
                  value={field.value || ""}
                  maxLength={item.maxLength}
                  isDisabled={item.isDisabled}
                />
              </FieldContainer>
            )}
          </Field>
        );
      case "checkbox":
        return (
          <Field name={item.name}>
            {({ field, form }) => (
              // <FieldContainer>
              <CheckboxField
                name={item.name}
                label={item.label}
                fullWidth
                isDisabled={item.isDisabled}
              />
              // </FieldContainer>
            )}
          </Field>
        );
      case "searchableSelect":
        return (
          <Field name={item.name}>
            {({ field }) => (
              <FieldContainer>
                <SearchableSelect
                  name={item.name}
                  label={item.label}
                  fullWidth
                  multiline
                  options={naicMembersWithoutAllValue()}
                  isDisabled={item.isDisabled}
                />
              </FieldContainer>
            )}
          </Field>
        );
      default:
        return (
          <Field name={item.name}>
            {({ field }) => (
              <FieldContainer>
                <InputField
                  name={item.name}
                  label={item.label}
                  value={field.value || ""}
                  maxLength={item.maxLength}
                  isDisabled={item.isDisabled}
                />
              </FieldContainer>
            )}
          </Field>
        );
    }
  };

  return (
    <FormWrapper>
      {isLoadingDeleteMember ||
        isLoading ||
        (isLoadingCreateMember && <LoadingPanel />)}
      <Box display={"flex"} sx={{ justifyContent: "space-between" }} mb={3}>
        <Box display={"flex"}>
          <ArrowBack onClick={() => navigate(-1)} color="primary" />
          <Typography variant="h1" color="primary.main">
            {`Member ${initialValues?.naicCode} - ${initialValues?.name}`}
          </Typography>
        </Box>
        <HeaderButtonsContainer>
          <Box onClick={() => setExpanded([1, 2, 3, 4, 5, 6])} type="button">
            <Typography variant="subtitle1">Expand all</Typography>
          </Box>
          <Typography variant="subtitle1"> &#8226; </Typography>
          <Box onClick={() => setExpanded([])} type="button">
            <Typography variant="subtitle1"> Collapse all</Typography>
          </Box>
        </HeaderButtonsContainer>
      </Box>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validationSchema={validationSchema}
      >
        {(field, form) => (
          <Form id={"memberForm"}>
            <FormContainer>
              <Accordion
                expanded={expanded.includes(1)}
                id={`accordion-1`}
                onChange={onAccordionClick(1)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`1-content`}
                  id={`1-header`}
                  sx={{ flexFlow: "row-reverse" }}
                >
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <HomeOutlined fontSize="small" />
                    <Typography variant="link">Name and Address</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <SectionContainer>
                    {getInputComponent({
                      name: "naicCode",
                      label: "NAIC Code:",
                      type: "number",
                      maxLength: 50,
                      isDisabled: !naicMemberRoleCheck(roleId),
                    })}
                    {getInputComponent({
                      name: "name",
                      label: "Name:",
                      type: "text",
                      maxLength: 256,
                      isDisabled: !naicMemberRoleCheck(roleId),
                    })}
                    {getInputComponent({
                      name: "address1",
                      label: "Address:",
                      type: "text",
                      maxLength: 256,
                    })}
                    {getInputComponent({
                      name: "address2",
                      label: "Address 2:",
                      type: "text",
                      maxLength: 256,
                    })}
                    {getInputComponent({
                      name: "city",
                      label: "City:",
                      type: "text",
                      maxLength: 256,
                    })}
                    {getInputComponent({
                      name: "state",
                      label: "State:",
                      type: "select",
                    })}

                    {getInputComponent({
                      name: "zipCode",
                      label: "Zip Code:",
                      type: "number",
                      maxLength: 50,
                    })}
                  </SectionContainer>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded.includes(2)}
                id={`accordion-2`}
                onChange={onAccordionClick(2)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`2-content`}
                  id={`2-header`}
                  sx={{ flexFlow: "row-reverse" }}
                >
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <InfoOutlined fontSize="small" />
                    <Typography variant="link">General Information</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <SectionContainer>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {getInputComponent({
                        name: "commenceDate",
                        label: "Commence Date:",
                        type: "date",
                        isDisabled: !naicMemberRoleCheck(roleId),
                      })}
                      {getInputComponent({
                        name: "businessType",
                        label: "Business Type:",
                        type: "text",
                        maxLength: 50,
                        isDisabled: !naicMemberRoleCheck(roleId),
                      })}
                      {getInputComponent({
                        name: "federalId",
                        label: "Federal ID:",
                        type: "text",
                        maxLength: 50,
                        isDisabled: !naicMemberRoleCheck(roleId),
                      })}
                      {getInputComponent({
                        name: "domicileState",
                        label: "Domicile State:",
                        type: "select",
                        isDisabled: !naicMemberRoleCheck(roleId),
                      })}
                      {getInputComponent({
                        name: "stateNumber",
                        label: "State Number:",
                        type: "text",
                        isDisabled: !naicMemberRoleCheck(roleId),
                      })}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <FieldContainer>
                        <FormLabel component="legend">Member Flags:</FormLabel>
                        {getInputComponent({
                          name: "insolventFlag",
                          label: "Insolvent",
                          type: "checkbox",
                          isDisabled: !naicMemberRoleCheck(roleId),
                        })}
                        {getInputComponent({
                          name: "suspendedFlag",
                          label: "Suspended",
                          type: "checkbox",
                          isDisabled: !naicMemberRoleCheck(roleId),
                        })}
                        {getInputComponent({
                          name: "abatementFlag",
                          label: "Abatement",
                          type: "checkbox",
                          isDisabled: !naicMemberRoleCheck(roleId),
                        })}
                        {getInputComponent({
                          name: "deferredFlag",
                          label: "Deferred",
                          type: "checkbox",
                          isDisabled: !naicMemberRoleCheck(roleId),
                        })}
                        {getInputComponent({
                          name: "activeFlag",
                          label: "Active",
                          type: "checkbox",
                          isDisabled: !naicMemberRoleCheck(roleId),
                        })}
                        {getInputComponent({
                          name: "mergedFlag",
                          label: "Merged",
                          type: "checkbox",
                          isDisabled: !getEditUsersPermission(roleId),
                        })}
                      </FieldContainer>

                      {field?.values?.mergedFlag && (
                        <>
                          {getInputComponent({
                            name: "mergedMemberValue",
                            label: "Merged Member:",
                            type: "searchableSelect",
                            isDisabled: !getEditUsersPermission(roleId),
                          })}
                          {getInputComponent({
                            name: "mergedEffectiveDate",
                            label: "Merged Effective Date:",
                            type: "date",
                            isDisabled: !getEditUsersPermission(roleId),
                          })}
                        </>
                      )}
                    </Box>
                  </SectionContainer>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded.includes(3)}
                id={`accordion-3`}
                onChange={onAccordionClick(3)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`3-content`}
                  id={`3-header`}
                  sx={{ flexFlow: "row-reverse" }}
                >
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <ContactsOutlined fontSize="small" />
                    <Typography variant="link">Contact Information</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <SectionContainer>
                    {getInputComponent({
                      name: "phone",
                      label: "Phone:",
                      type: "text",
                      maxLength: 50,
                    })}
                    {getInputComponent({
                      name: "attention",
                      label: "Attention:",
                      type: "text",
                      maxLength: 50,
                    })}
                    {getInputComponent({
                      name: "assessmentEmail",
                      label: "Assessment Email:",
                      type: "textarea",
                      maxLength: 250,
                    })}

                    {getInputComponent({
                      name: "proxyEmail",
                      label: "Proxy Email:",
                      type: "textarea",
                      maxLength: 250,
                    })}
                  </SectionContainer>
                </AccordionDetails>
              </Accordion>

              {/* <Accordion
                expanded={expanded.includes(4)}
                id={`accordion-4`}
                onChange={onAccordionClick(4)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`4-content`}
                  id={`4-header`}
                  sx={{ flexFlow: "row-reverse" }}
                >
                  <Typography variant="link">
                    Current Account Information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {getInputComponent({
                    name: "currentAccountInfo",
                    label: "Balance Due:",
                    type: "amount",
                    isDisabled: !naicMemberRoleCheck(roleId),
                  })}
                </AccordionDetails>
              </Accordion> */}
              <Accordion
                expanded={expanded.includes(5)}
                id={`accordion-4`}
                onChange={onAccordionClick(5)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`5-content`}
                  id={`5-header`}
                  sx={{ flexFlow: "row-reverse" }}
                >
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <ReceiptLong fontSize="small" />
                    <Typography variant="link">Invoices</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <div style={{ display: "flex", marginBottom: "10px" }}>
                    {getInputComponent({
                      name: "balanceDue",
                      label: "Balance Due:",
                      type: "amount",
                      isDisabled: true,
                    })}
                  </div>
                  <InvoicesTab
                    isMemberTab
                    onRedirect={onRedirect}
                    memberNaicCode={initialValues?.naicCode}
                    disableFilterReset={disableFilterReset}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded.includes(6)}
                id={`accordion-4`}
                onChange={onAccordionClick(6)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`6-content`}
                  id={`6-header`}
                  sx={{ flexFlow: "row-reverse" }}
                >
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <Paid fontSize="small" />
                    <Typography variant="link">Payments</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <PaymentsTab
                    isMemberTab
                    onRedirect={onRedirect}
                    memberNaicCode={initialValues?.naicCode}
                    disableFilterReset={disableFilterReset}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded.includes(8)}
                id={`accordion-6`}
                onChange={onAccordionClick(8)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`8-content`}
                  id={`8-header`}
                  sx={{ flexFlow: "row-reverse" }}
                >
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <WorkspacePremiumOutlined fontSize="small" />
                    <Typography variant="link">Licenses</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {getInputComponent({
                    name: "licenseType",
                    label: "License Type:",
                    type: "licenseType",
                  })}
                  <LicensesTab
                    naicMemberRoleCheck={naicMemberRoleCheck(roleId)}
                    naicCode={initialValues?.naicCode}
                    memberKey={initialValues?.id}
                    licensesTypes={licensesTypes}
                    licensesType={field.values?.licenseType}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded.includes(7)}
                id={`accordion-5`}
                onChange={onAccordionClick(7)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`7-content`}
                  id={`7-header`}
                  sx={{ flexFlow: "row-reverse" }}
                >
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <History fontSize="small" />
                    <Typography variant="link">Member History</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <MemberHistoryTab
                    naicMemberRoleCheck={naicMemberRoleCheck(roleId)}
                    naicCode={initialValues?.naicCode}
                    memberKey={initialValues?.id}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded.includes(9)}
                id={`accordion-9`}
                onChange={onAccordionClick(9)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`9-content`}
                  id={`9-header`}
                  sx={{ flexFlow: "row-reverse" }}
                >
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <GroupsOutlined fontSize="small" />
                    <Typography variant="link">Affiliates</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="link" mb={"10px"} display={"flex"}>
                    Group: {data?.groupCode} - {data?.groupName}
                  </Typography>
                  <ChangeGroupMemberPopup
                    currentGroupId={data ? {id:data?.groupId, codeName:`${data?.groupCode} - ${data?.groupName}`} : null}
                    memberId={id}
                  />
                  {affiliatesExpanded && (
                    <StyledGrid
                      data={affiliatesData}
                      width="100%"
                      columns={affiliatesColumns}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            </FormContainer>
            <ButtonsContainer>
              <Button variant="outlined" type="submit">
                Save
              </Button>
              <Button variant="outlined" type="reset">
                Cancel
              </Button>
              {/* {type === "edit" && (
                <Button variant="outlined" onClick={onDeleteMemberClick}>
                  Delete
                </Button>
              )} */}
            </ButtonsContainer>
          </Form>
        )}
      </Formik>
      <InfoPopup
        isOpen={isOpenPopup}
        header="Delete member"
        text="Are you sure you want to delete member?"
        handleClose={() => {
          setIsOpenPopup(false);
        }}
        onClick={handleDeleteMember}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </FormWrapper>
  );
};

export default MemberForm;
